import { Component } from '@theme/component';

/**
 * A small dismissible nudge tooltip that points at the Size Guide trigger, encouraging shoppers to
 * open it. Dismissing it — by clicking its own close button, or by opening the size guide dialog —
 * only hides it temporarily: it reappears after `data-reappear-delay` seconds (see
 * `blocks/size-guide.liquid`'s "Hint reappear delay" setting), on a repeating loop, for as long as
 * the page stays open.
 *
 * @extends {Component}
 */
class SizeGuideHint extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.querySelector('dialog-component')?.addEventListener('dialog:open', this.dismiss);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.querySelector('dialog-component')?.removeEventListener('dialog:open', this.dismiss);
    clearTimeout(this.#reappearTimeoutId);
  }

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #reappearTimeoutId;

  /**
   * Hides the hint, then schedules it to reappear after the configured delay.
   */
  dismiss = () => {
    const bubble = this.querySelector('.size-guide__hint');
    if (!(bubble instanceof HTMLElement) || bubble.hidden) return;

    bubble.hidden = true;

    const delaySeconds = Number(this.dataset.reappearDelay) || 3;
    this.#reappearTimeoutId = setTimeout(() => {
      bubble.hidden = false;
    }, delaySeconds * 1000);
  };

  /**
   * Opens the size guide from the bubble's own text — the nudge points at the guide, so the text is
   * the obvious thing to click. (The × next to it keeps dismissing.) Opening it also dismisses the
   * bubble, through the `dialog:open` listener above, exactly as using the trigger does.
   */
  openGuide = () => {
    const dialog = this.querySelector('dialog-component');
    if (dialog && 'showDialog' in dialog && typeof dialog.showDialog === 'function') dialog.showDialog();
  };

  /**
   * Keyboard activation for the bubble text, which is a `role="button"` div (its content is rich text,
   * which a real <button> cannot hold).
   * @param {KeyboardEvent} event
   */
  onHintKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.openGuide();
  };
}

if (!customElements.get('size-guide-hint')) customElements.define('size-guide-hint', SizeGuideHint);
